import type { components } from '@/api/generated/schema';
import { apiJson, ApiClientError } from '@/api/client';

export type AccountData = components['schemas']['AccountData'];

// With BFF session (HttpOnly cookie), we no longer store tokens in localStorage.
// The auth flow is now: GET /api/auth/login → redirect to OIDC → callback → HttpOnly session.

export const currentAccount = async (signal?: AbortSignal): Promise<AccountData | null> => {
  // BFF session is held in HttpOnly cookies, verified via backend.
  // Read the current verified account without a side effect.
  try {
    // Callers decide what null means: the landing page stays usable and
    // RequireAuth redirects to /login itself.
    return await apiJson<AccountData>('/api/auth/account', { signal }, { redirectOnUnauthorized: false });
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) {
      return null;
    }
    throw error;
  }
};

export const isAuthenticated = async (signal?: AbortSignal): Promise<boolean> =>
  (await currentAccount(signal)) !== null;

export const logout = async (): Promise<void> => {
  // Use browser form POST to /api/auth/logout for proper IdP logout flow.
  // The backend returns 303 redirect to IdP logout endpoint.
  // This ensures the session is ended server-side and the browser can follow.
  if (typeof window !== 'undefined') {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/auth/logout';
    document.body.appendChild(form);
    form.submit();
  }
};
